import { useLayoutEffect, useState } from 'react'
import pkg from '../../package.json'
import { ConnectionPanel } from '@/features/dashboard/components/ConnectionPanel'
import { SceneViewport } from '@/features/avatar/SceneViewport'
import { BleStatusIndicator } from '@/features/dashboard/components/BleStatusIndicator'
import { useBle } from '@/features/ble/hooks/useBle'
import { useUiStore, useResolvedTheme, type LabScene, type Theme } from '@/state/uiStore'

type LabLayoutProps = {
  onOpenPrivacyPanel: () => void
  onOpenCookiePanel: () => void
}

const SCENE_LABELS: Record<LabScene, string> = {
  default: 'Sandbox',
  health: 'Health',
  robotics: 'Robotics',
}

const THEME_CYCLE: Theme[] = ['light', 'dark', 'system']

/**
 * LabLayout is the root shell of NEENO Lab.
 * It owns the primary spatial layout: sidebar + 3D viewport.
 */
export function LabLayout({ onOpenPrivacyPanel, onOpenCookiePanel }: LabLayoutProps) {
  const [settingsOpen, setSettingsOpen] = useState(false)
  const scene = useUiStore((s) => s.scene)
  const setScene = useUiStore((s) => s.setScene)
  const theme = useUiStore((s) => s.theme)
  const setTheme = useUiStore((s) => s.setTheme)
  const sidebarOpen = useUiStore((s) => s.sidebarOpen)
  const toggleSidebar = useUiStore((s) => s.toggleSidebar)
  const frameCorrection = useUiStore((s) => s.frameCorrection)
  const toggleFrameCorrection = useUiStore((s) => s.toggleFrameCorrection)
  const { status: bleStatus } = useBle()
  const resolvedTheme = useResolvedTheme()
  const nextTheme = THEME_CYCLE[(THEME_CYCLE.indexOf(theme) + 1) % THEME_CYCLE.length]

  // Sync the resolved theme to the HTML element so CSS variables take effect
  useLayoutEffect(() => {
    document.documentElement.dataset.theme = resolvedTheme
  }, [resolvedTheme])

  return (
    <div className="flex h-screen w-screen flex-col overflow-hidden bg-canvas font-sans text-primary">
      {/* Top bar */}
      <header className="relative z-20 flex h-14 shrink-0 items-center justify-between gap-3 border-b border-edge/70 bg-surface px-4 sm:px-5">
        <div className="flex items-center gap-3">
          <img
            src="/Neeno-logo-blue-150px.png"
            alt="NEENO"
            className="h-5 w-auto select-none"
            draggable={false}
          />
          <span aria-hidden="true" className="h-4 w-px bg-edge" />
          <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.3em] text-accent">
            Lab
          </span>
        </div>

        <div className="flex items-center gap-2">
          <label className="relative flex items-center text-muted">
            <span className="sr-only">Select scene</span>
            <select
              value={scene}
              onChange={(event) => setScene(event.target.value as LabScene)}
              aria-label="Select scene"
              className="appearance-none rounded-full border border-edge bg-transparent py-1.5 pl-3.5 pr-8 font-mono text-[10px] uppercase tracking-[0.14em] text-primary transition duration-200 hover:border-primary/40"
            >
              {Object.entries(SCENE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2"
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </label>

          <a
            href="https://neeno.io"
            target="_blank"
            rel="noreferrer"
            className="pill hidden sm:inline-flex"
          >
            neeno.io
          </a>

          {/* Theme toggle — cycles light → dark → system */}
          <button
            type="button"
            onClick={() => setTheme(nextTheme)}
            aria-label={`Switch to ${nextTheme} theme`}
            title={`Switch to ${nextTheme} theme`}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-edge text-secondary transition duration-200 hover:border-primary/40 hover:text-primary active:scale-95"
          >
            {theme === 'dark' ? (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 12.79A9 9 0 1 1 11.21 3c0 4.97 4.03 9 9 9Z" />
              </svg>
            ) : theme === 'system' ? (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <rect x="2" y="3" width="20" height="14" rx="2" />
                <line x1="8" y1="21" x2="16" y2="21" />
                <line x1="12" y1="17" x2="12" y2="21" />
              </svg>
            ) : (
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="4" />
                <path d="M12 2v2" />
                <path d="M12 20v2" />
                <path d="m4.93 4.93 1.41 1.41" />
                <path d="m17.66 17.66 1.41 1.41" />
                <path d="M2 12h2" />
                <path d="M20 12h2" />
                <path d="m6.34 17.66-1.41 1.41" />
                <path d="m19.07 4.93-1.41 1.41" />
              </svg>
            )}
          </button>
        </div>
      </header>

      {/* Main area */}
      <div className="flex flex-1 overflow-hidden">
        {/* Left sidebar */}
        <div
          className={`flex shrink-0 flex-col overflow-hidden border-r border-edge/70 bg-surface transition-[width] duration-200 ${sidebarOpen ? 'w-60 sm:w-[17.5rem]' : 'w-12 sm:w-14'}`}
        >
          <div
            className={`flex ${sidebarOpen ? 'justify-end px-3 pt-3' : 'flex-col items-center gap-3 pt-3'}`}
          >
            <button
              onClick={toggleSidebar}
              aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
              className="flex h-8 w-8 items-center justify-center rounded-full text-muted transition duration-200 hover:bg-inset hover:text-primary active:scale-95"
            >
              {sidebarOpen ? (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              ) : (
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              )}
            </button>

            {!sidebarOpen && (
              <div aria-label="BLE status dot" className="flex items-center justify-center">
                <BleStatusIndicator
                  status={bleStatus}
                  showLabel={false}
                  className="justify-center"
                />
              </div>
            )}
          </div>

          <div
            className={`flex-1 overflow-y-auto p-3 transition-opacity duration-150 ${sidebarOpen ? 'opacity-100' : 'pointer-events-none select-none opacity-0'}`}
            aria-hidden={!sidebarOpen}
          >
            <ConnectionPanel />
          </div>

          <div
            className={`border-t border-edge/70 px-3 pt-3 transition-opacity duration-150 ${sidebarOpen ? 'opacity-100' : 'pointer-events-none select-none opacity-0'}`}
            aria-hidden={!sidebarOpen}
          >
            <div className="mb-3 rounded-2xl border border-edge/60 bg-canvas/40 p-3">
              <div className="flex items-center justify-between">
                <div className="eyebrow">Settings</div>
                <button
                  type="button"
                  aria-label={settingsOpen ? 'Collapse settings' : 'Expand settings'}
                  onClick={() => setSettingsOpen((open) => !open)}
                  className="flex h-6 w-6 items-center justify-center rounded-full text-muted transition duration-200 hover:text-primary"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`transition-transform duration-200 ${settingsOpen ? 'rotate-180' : ''}`}
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>
              </div>
              {settingsOpen && (
                <div className="mt-3 flex flex-col gap-2">
                  <button
                    type="button"
                    onClick={toggleFrameCorrection}
                    className={`w-full rounded-full border px-3.5 py-2 text-left font-mono text-[10px] font-medium uppercase tracking-[0.14em] transition duration-200 ${
                      frameCorrection
                        ? 'border-accent/50 bg-accent/10 text-accent'
                        : 'border-edge text-muted hover:border-primary/40 hover:text-primary'
                    }`}
                  >
                    Frame Correction: {frameCorrection ? 'On' : 'Off'}
                  </button>
                </div>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={onOpenPrivacyPanel}
                className="pill w-full justify-start"
              >
                Privacy
              </button>
              <button
                type="button"
                onClick={onOpenCookiePanel}
                className="pill w-full justify-start"
              >
                Cookie Settings
              </button>
            </div>
          </div>

          <div
            className={`border-t border-edge/70 px-3 py-2.5 text-center font-mono text-[10px] tracking-[0.08em] text-muted transition-opacity duration-150 ${sidebarOpen ? 'opacity-100' : 'pointer-events-none select-none opacity-0'}`}
            aria-hidden={!sidebarOpen}
          >
            NEENO Lab v{pkg.version}
          </div>
        </div>

        {/* 3D Viewport — fills remaining space */}
        <main className="relative flex-1 overflow-hidden">
          <SceneViewport />
        </main>
      </div>
    </div>
  )
}
